import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, FileText, RotateCcw, Save } from 'lucide-react';

const TABS = [
  { id: 'letter', label: 'Letter (print)' },
  { id: 'mail1', label: 'Mail 1 (sequence)' },
  { id: 'mail2', label: 'Mail 2 (sequence)' },
  { id: 'breakup', label: 'Break-up letter' },
];

export function LetterTemplateDialog({ canEdit }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [letterFrame, setLetterFrame] = useState('');
  const [breakupFrame, setBreakupFrame] = useState('');
  const [mail1Subject, setMail1Subject] = useState('');
  const [mail1Frame, setMail1Frame] = useState('');
  const [mail2Frame, setMail2Frame] = useState('');
  const [tab, setTab] = useState('letter');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.getLetterTemplate().then(r => {
      setMeta(r.data);
      setLetterFrame(r.data.letter_frame);
      setBreakupFrame(r.data.breakup_frame || '');
      setMail1Subject(r.data.seq_mail1_subject || '');
      setMail1Frame(r.data.seq_mail1_frame || '');
      setMail2Frame(r.data.seq_mail2_frame || '');
    }).catch(err => toast.error(err.response?.data?.detail || 'Failed to load standard text'));
  }, [open]);

  const save = async () => {
    if (!letterFrame.trim() || !breakupFrame.trim()) { toast.error('All texts are required'); return; }
    if (!mail1Subject.trim() || !mail1Frame.trim() || !mail2Frame.trim()) { toast.error('Sequence Mail 1 (subject + text) and Mail 2 are required'); return; }
    setSaving(true);
    try {
      const r = await api.saveLetterTemplate({
        letter_frame: letterFrame, breakup_frame: breakupFrame,
        seq_mail1_subject: mail1Subject, seq_mail1_frame: mail1Frame, seq_mail2_frame: mail2Frame,
      });
      const nb = r.data?.breakup_applied?.enrollments || 0;
      const nm = r.data?.seq_mails_applied?.enrollments || 0;
      const n = Math.max(nb, nm);
      toast.success(n > 0
        ? `Standard text saved — applied to ${n} running sequence${n === 1 ? '' : 's'}`
        : 'Standard text saved — all new letters & sequence mails will use it');
      setOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm('Reset all texts (letter, email, sequence mails, break-up letter) to the original OLTA default?')) return;
    setSaving(true);
    try {
      const r = await api.saveLetterTemplate({ reset: true });
      setLetterFrame(r.data.letter_frame);
      setBreakupFrame(r.data.breakup_frame || '');
      setMail1Subject(r.data.seq_mail1_subject || '');
      setMail1Frame(r.data.seq_mail1_frame || '');
      setMail2Frame(r.data.seq_mail2_frame || '');
      setMeta(m => ({ ...m, customized: false, updated_by: '' }));
      toast.success('Reset to default');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset'); }
    setSaving(false);
  };

  const hint = () => {
    if (tab === 'breakup') return (
      <>
        The standardized <span className="text-amber-300">break-up letter</span> — final sequence step, printed via iAuto. It is used word for word; placeholders like
        <span className="text-sky-300 font-mono"> {'{anrede}'} {'{company}'} {'{full_name}'} </span>
        are filled per contact. Saving updates the sequence templates and all pending break-up letters of running sequences (drafts included).
      </>
    );
    if (tab === 'mail1') return (
      <>
        <span className="text-sky-300">Sequence Mail 1</span> — the first automatic email, sent ~7 days after the printed letter. Used word for word; placeholders like
        <span className="text-sky-300 font-mono"> {'{herr_frau}'} {'{anrede}'} {'{company}'} </span>
        are filled per contact ({'{herr_frau}'} → "Herr/Frau Nachname"). Saving updates the sequence templates and every not-yet-sent Mail 1 of running sequences.
      </>
    );
    if (tab === 'mail2') return (
      <>
        <span className="text-sky-300">Sequence Mail 2</span> — the second automatic email. It is sent as a <span className="text-emerald-300">real reply to Mail 1</span> (same thread, subject "Re: …"), so no separate subject is needed. Saving updates the sequence templates and every not-yet-sent Mail 2 of running sequences.
      </>
    );
    return (
      <>
        This is the base text the AI uses for every generated letter. Parts marked
        <span className="text-sky-300 font-mono"> **[bold in brackets]** </span>
        are personalized per company by the AI — everything else is taken over word for word.
      </>
    );
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="ghost" data-testid="letter-template-btn"
        className="border border-border text-foreground hover:bg-secondary rounded-sm text-sm h-9 px-4">
        <FileText className="w-4 h-4 mr-2" /> Standard Text
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="letter-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-400" /> Standard Outreach Text
            </DialogTitle>
          </DialogHeader>
          {!meta ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-3">
              <div className="bg-secondary/40 border border-border rounded-sm p-3 text-xs text-muted-foreground">
                {hint()}
                {meta.customized && meta.updated_by && (
                  <span className="block mt-1 text-emerald-300">Customized by {meta.updated_by}{meta.updated_at ? ` on ${new Date(meta.updated_at).toLocaleDateString('de-DE')}` : ''}.</span>
                )}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {TABS.map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)} data-testid={`template-tab-${t.id}`}
                    className={`text-xs rounded-sm px-3 h-8 border transition-colors ${tab === t.id
                      ? (t.id === 'breakup' ? 'border-amber-500/50 bg-amber-600/10 text-amber-300' : 'border-sky-500/50 bg-sky-600/10 text-sky-300')
                      : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {tab === 'letter' && (
                <Textarea value={letterFrame} onChange={e => setLetterFrame(e.target.value)} rows={18}
                  disabled={!canEdit} data-testid="template-letter-textarea"
                  className="bg-secondary border-border text-foreground rounded-sm text-xs font-mono leading-relaxed" />
              )}
              {tab === 'mail1' && (
                <div className="space-y-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Subject</label>
                    <Input value={mail1Subject} onChange={e => setMail1Subject(e.target.value)} disabled={!canEdit}
                      data-testid="template-mail1-subject-input"
                      className="bg-secondary border-border text-foreground rounded-sm text-xs font-mono h-9 mt-1" />
                  </div>
                  <Textarea value={mail1Frame} onChange={e => setMail1Frame(e.target.value)} rows={16}
                    disabled={!canEdit} data-testid="template-mail1-textarea"
                    className="bg-secondary border-border text-foreground rounded-sm text-xs font-mono leading-relaxed" />
                </div>
              )}
              {tab === 'mail2' && (
                <div className="space-y-2">
                  <p className="text-[10px] text-muted-foreground">Subject: <span className="text-foreground font-mono">Re: {mail1Subject || '…'}</span> — automatic reply to Mail 1 in the same thread</p>
                  <Textarea value={mail2Frame} onChange={e => setMail2Frame(e.target.value)} rows={16}
                    disabled={!canEdit} data-testid="template-mail2-textarea"
                    className="bg-secondary border-border text-foreground rounded-sm text-xs font-mono leading-relaxed" />
                </div>
              )}
              {tab === 'breakup' && (
                <Textarea value={breakupFrame} onChange={e => setBreakupFrame(e.target.value)} rows={14}
                  disabled={!canEdit} data-testid="template-breakup-textarea"
                  className="bg-secondary border-border text-foreground rounded-sm text-xs font-mono leading-relaxed" />
              )}
              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={reset} disabled={saving || !canEdit || !meta.customized}
                  data-testid="template-reset-btn"
                  className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-9">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to default
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}
                    className="border-border text-muted-foreground rounded-sm text-xs h-9">Close</Button>
                  {canEdit && (
                    <Button onClick={save} disabled={saving} data-testid="template-save-btn"
                      className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-9 px-4">
                      {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
